import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import RestaurantSettingComponent from "../../components/RestaurantSetting/RestaurantSettingComponent";
import SubcriptionSettinf from "../../components/SubcriptionSetting/SubcriptionSetting";
import DeliveryPolicy from "src/components/DeliveryPolicy/DeliveryPolicy";
import ChannelTab from "src/features/Channel/Channel";
import PrinterSettings from "src/components/PrinterSettings/PrinterSettings";
import TerminalSettings from "src/components/TerminalSettings/TerminalSettings";
import StationsSettings from "src/components/StationsSettings/StationsSettings";
import CardProcessingFeeSettings from "src/components/CardProcessingFeeSettings/CardProcessingFeeSettings";
import LanguageSettings from "src/components/LanguageSettings/LanguageSettings";
import OrderingSettings from "src/components/OrderingSettings/OrderingSettings";
import EnterpriseCapacitySettings from "src/components/EnterpriseCapacitySettings/EnterpriseCapacitySettings";
import TipSettings from "src/components/TipSettings/TipSettings";
import MessagingSettingsPage from "src/features/MessagingSettings/MessagingSettingsPage";
import { PayrollAndTipsSettings } from "src/features/Payroll/components/PayrollAndTipsSettings";
import PaymentAccountSetting from "src/components/PaymentAccountSetting/PaymentAccountSetting";
import { useAuth } from "src/contexts/auth-provider";
import { FEATURE_NAMES } from "src/utils/isAllowedFeature";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  GET_STORE_FEATURES,
  GET_CLIENT_DOWNLOAD_BUILDS,
  GET_CLIENT_DOWNLOAD_URL_BY_BUILD_ID,
} from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { SettingsSubmenu, type SettingsTabId } from "./SettingsSubmenu";
import DownloadIcon from "../../assests/images/dashboard/Container.png";
import { failureToast } from "src/utils/toasts";

type ClientPlatform = "WINDOWS" | "MACOS";

interface ClientDownloadBuild {
  correlationId: string;
  platform: ClientPlatform;
  channel: "STABLE" | "BETA";
  version: string;
  fileName: string;
  sizeBytes?: number | null;
  /** UTC epoch milliseconds from GraphQL Long */
  uploadedAt: number | null;
  isLatest: boolean;
}

const RestaurantSetting = () => {
  const [activeTab, setActiveTab] = useState<SettingsTabId>("profile");
  const [searchParams] = useSearchParams();
  const [clientBuilds, setClientBuilds] = useState<ClientDownloadBuild[]>([]);
  const [loadingBuilds, setLoadingBuilds] = useState(false);
  const [downloadingBuildId, setDownloadingBuildId] = useState<string | null>(null);
  const { permissions, hasRestaurant, sethasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const isOwnerAtStore = hasRestaurant?.storeStaffRole === "OWNER";

  useEffect(() => {
    const tab = searchParams.get("tab") as SettingsTabId | null;
    if (tab === "paymentAccount" || tab === "subscription") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchClientBuilds = async () => {
    setLoadingBuilds(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_CLIENT_DOWNLOAD_BUILDS,
        variables: {},
      });
      const builds = data?.data?.clientDownloadBuilds;
      setClientBuilds(Array.isArray(builds) ? builds : []);
    } catch (err) {
      console.error("Failed to load client builds:", err);
      failureToast("Unable to load client builds.");
    } finally {
      setLoadingBuilds(false);
    }
  };

  const handleBuildDownload = async (build: ClientDownloadBuild) => {
    setDownloadingBuildId(build.correlationId);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_CLIENT_DOWNLOAD_URL_BY_BUILD_ID,
        variables: {
          buildId: build.correlationId,
        },
      });
      const downloadUrl = data?.data?.clientDownloadUrl;
      if (!downloadUrl) throw new Error("No URL returned from backend");
      window.location.href = downloadUrl;
    } catch (err) {
      console.error("Download failed:", err);
      failureToast("Sorry, the file could not be downloaded.");
    } finally {
      setDownloadingBuildId(null);
    }
  };

  useEffect(() => {
    if (activeTab === "downloadClient") {
      fetchClientBuilds();
    }
  }, [activeTab]);

  const formatBuildSize = (sizeBytes?: number | null) => {
    if (!sizeBytes || sizeBytes <= 0) return "—";
    const mb = sizeBytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatUploadedAt = (uploadedAt: number | null | undefined) => {
    if (uploadedAt == null || typeof uploadedAt !== "number") return "—";
    const date = new Date(uploadedAt);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  };

  const groupedBuilds: Record<ClientPlatform, ClientDownloadBuild[]> = {
    WINDOWS: clientBuilds.filter((build) => build.platform === "WINDOWS"),
    MACOS: clientBuilds.filter((build) => build.platform === "MACOS"),
  };

  // When Channel tab is hidden (no permission), or Subscription/Payroll when not owner, switch away
  useEffect(() => {
    if (activeTab === "channel" && !permissions[FEATURE_NAMES.ChannelPriceOverride]) {
      setActiveTab("profile");
    }
    if ((activeTab === "subscription" || activeTab === "paymentAccount" || activeTab === "payrollAndTips") && !isOwnerAtStore) {
      setActiveTab("profile");
    }
  }, [activeTab, permissions, isOwnerAtStore]);

  // Refresh store userAllowedFeatures from backend when entering this page so tip/station/terminal tabs match current plan
  useEffect(() => {
    if (!hasRestaurant?.correlationId) return;
    const fetchStoreFeatures = async () => {
      try {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_STORE_FEATURES,
          variables: {
            retrieveStoresInput: { storeCorrelationIds: [hasRestaurant.correlationId] },
          },
        });
        const stores = data?.data?.stores;
        const store = Array.isArray(stores) && stores.length > 0 ? stores[0] : null;
        if (store && (store.userAllowedFeatures != null || store.planId != null)) {
          sethasRestaurant({ ...hasRestaurant, userAllowedFeatures: store.userAllowedFeatures ?? hasRestaurant.userAllowedFeatures, storeAllowedFeatures: store.storeAllowedFeatures ?? hasRestaurant.storeAllowedFeatures, planId: store.planId ?? hasRestaurant.planId });
          if (typeof localStorage !== "undefined") {
            const saved = JSON.parse(localStorage.getItem("restaurant") || "{}");
            if (saved?.correlationId === hasRestaurant.correlationId) {
              localStorage.setItem("restaurant", JSON.stringify({ ...saved, userAllowedFeatures: store.userAllowedFeatures ?? saved.userAllowedFeatures, storeAllowedFeatures: store.storeAllowedFeatures ?? saved.storeAllowedFeatures, planId: store.planId ?? saved.planId }));
            }
          }
        }
      } catch (_) {
        // Keep current hasRestaurant on error
      }
    };
    fetchStoreFeatures();
  }, [hasRestaurant?.correlationId]);

  return (
    <div className="w-full max-w-full flex-1 min-h-0 min-w-0 flex flex-col pt-0 pb-0">
      {/* Settings: stack nav above content below lg so the main pane gets full width */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 min-w-0 rounded-2xl border border-[#e5e7eb] bg-[#f1f5f9] overflow-hidden">
        <SettingsSubmenu
          activeTab={activeTab}
          onSelect={setActiveTab}
          permissions={permissions}
          FEATURE_NAMES={FEATURE_NAMES}
          isOwnerAtStore={isOwnerAtStore}
        />
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden bg-white border-t border-[#e5e7eb] lg:border-t-0 lg:border-l">
          <div className="pt-4 sm:pt-6 pb-4 sm:pb-6">
            {(activeTab === "hours" || activeTab === "profile") && (
              <RestaurantSettingComponent tab={activeTab} />
            )}
            {activeTab === "subscription" && isOwnerAtStore && <SubcriptionSettinf />}
            {activeTab === "paymentAccount" && isOwnerAtStore && <PaymentAccountSetting />}
            {activeTab === "tip" && permissions[FEATURE_NAMES.AdvancedTipSettings] && <TipSettings />}
            {activeTab === "payrollAndTips" && isOwnerAtStore && <PayrollAndTipsSettings />}
            {activeTab === "deliveryPolicy" && <DeliveryPolicy />}
            {activeTab === "ordering" && <OrderingSettings />}
            {activeTab === "enterpriseCapacity" && <EnterpriseCapacitySettings />}
            {activeTab === "channel" && permissions[FEATURE_NAMES.ChannelPriceOverride] && <ChannelTab />}
            {activeTab === "printer" && <PrinterSettings />}
            {activeTab === "cardProcessingFee" && <CardProcessingFeeSettings />}
            {activeTab === "textMessaging" && <MessagingSettingsPage />}
            {activeTab === "language" && <LanguageSettings />}
            {activeTab === "terminalsAndStations" && permissions[FEATURE_NAMES.MultipleStationsAndTerminals] && (
              <div className="w-full max-w-[760px] space-y-3">
                <TerminalSettings />
                <StationsSettings />
              </div>
            )}
            {activeTab === "downloadClient" && (
              <div className="w-full max-w-[760px]">
                <div className="bg-white rounded-[16px] px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_20px_rgba(0,0,0,0.08)]">
                  <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Download Client</h2>
                  <p className="text-[14px] text-[#6b7280] mb-4">
                    Download a specific build for Windows or macOS. Latest builds are marked, and older versions remain available.
                  </p>

                  {loadingBuilds ? (
                    <div className="text-sm text-[#6b7280]">Loading available builds...</div>
                  ) : (
                    <div className="space-y-5">
                      {(["WINDOWS", "MACOS"] as ClientPlatform[]).map((platform) => (
                        <div key={platform} className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                          <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                            <h3 className="text-sm font-semibold text-[#111827]">{platform === "WINDOWS" ? "Windows" : "macOS"}</h3>
                          </div>
                          <div className="divide-y divide-[#E5E7EB]">
                            {groupedBuilds[platform].length > 0 ? (
                              groupedBuilds[platform].map((build) => (
                                <div key={build.correlationId} className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className="text-sm font-semibold text-[#111827]">{build.version}</span>
                                      {build.isLatest && (
                                        <span className="inline-flex items-center rounded-full bg-[#fef3ef] text-[#FD7657] px-2 py-0.5 text-xs font-semibold">
                                          Latest
                                        </span>
                                      )}
                                      <span className="inline-flex items-center rounded-full bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5 text-xs font-medium">
                                        {build.channel}
                                      </span>
                                    </div>
                                    <div className="text-xs text-[#6B7280] break-all">{build.fileName}</div>
                                    <div className="text-xs text-[#6B7280] mt-1">
                                      Uploaded: {formatUploadedAt(build.uploadedAt)} • Size: {formatBuildSize(build.sizeBytes)}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleBuildDownload(build)}
                                    disabled={downloadingBuildId === build.correlationId}
                                    className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded-lg text-sm font-medium disabled:opacity-50 bg-[rgba(249,115,22,0.08)] text-[#f97316] hover:bg-[rgba(249,115,22,0.12)]"
                                  >
                                    <img className="h-[15px] w-[15px]" src={DownloadIcon} alt="" />
                                    {downloadingBuildId === build.correlationId ? "Preparing..." : "Download"}
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-[#6B7280]">
                                No {platform === "WINDOWS" ? "Windows" : "macOS"} builds registered yet.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!loadingBuilds && clientBuilds.length === 0 && (
                    <div className="mt-5 border border-dashed border-[#E5E7EB] rounded-xl p-4 bg-[#FAFAFA]">
                      <p className="text-sm text-[#6B7280]">
                        No desktop builds are available yet. When your team publishes installers to the release catalog,
                        they will appear here for download.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantSetting;
